import { useEffect, useState } from 'react'
import { useParams, Link, useSearchParams  } from 'react-router-dom'
import { CheckCircle, Calendar, Clock, MapPin, Download, ExternalLink, Video  } from 'lucide-react'
import api from '../services/api'

export default function ConfirmationPage() {
  const { token } = useParams()
  const [searchParams] = useSearchParams()
  const [appointment, setAppointment] = useState(null)
  const [profile, setProfile] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const paymentStatus = searchParams.get('payment') // 'success' or 'failed' or null

  useEffect(() => {
    api.get(`/api/appointments/by-token/${token}`)
      .then(res => {
          setAppointment(res.data);
          
          // 👑 LE CHANGEMENT DE CONTEXTE MAGIQUE
          // On force le Frontend à utiliser le Workspace du RDV pour la suite de la navigation
          api.defaults.headers.common['X-Workspace-Id'] = res.data.workspace_id;
          
          // On prévient le Layout (Bandeau/Footer) qu'il doit recharger ses couleurs et son nom !
          window.dispatchEvent(new Event('workspaceChanged'));
          
          // MAINTENANT que le contexte est bon, on charge le profil (Logo, nom, etc.)
          api.get('/api/workspaces/public-profile')
             .then(pRes => setProfile(pRes.data))
             .catch(() => {});
      })
      .catch(err => setError(err.response?.data?.detail || 'Rendez-vous non trouvé'))
      .finally(() => setLoading(false))
  }, [token])


  const getConsultationLocation = (type) => {
    switch(type) {
      case 'cabinet': return `${profile.cabinet_name || 'Cabinet'}, ${profile.cabinet_adresse || ''}` // <-- DYNAMIQUE
      case 'video': return 'Téléconsultation vidéo'
      case 'domicile': return 'À votre domicile'
      default: return type
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-32">
        <div className="spinner" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="card">
          <p className="text-red-500 mb-4">{error}</p>
          <Link to="/" className="btn btn-primary">Retour à l'accueil</Link>
        </div>
      </div>
    )
  }

  // On calcule si le RDV est dans le futur
  const isFuture = new Date(`${appointment.date}T${appointment.start_time}`) > new Date();

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="card text-center">
        {/* MESSAGE SPECIAL PAIEMENT */}
        {paymentStatus === 'success' && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-800 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                <p className="font-bold text-lg">Paiement reçu avec succès ! 💳</p>
                <p className="text-sm">{isFuture ? "Votre rendez-vous est définitivement validé." : "Votre facture est à jour."}</p>
            </div>
        )}
        
        {/* Titres contextuels selon si le RDV est passé ou futur */}
        {isFuture ? (
            <>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h1 className="text-3xl font-serif text-sage-700 mb-2">
                  Rendez-vous confirmé !
                </h1>
                <p className="text-sage-600 mb-8">
                  Merci pour votre confiance. Un email de confirmation vous a été envoyé.
                </p>
            </>
        ) : (
            <>
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                  <Calendar className="w-10 h-10 text-gray-400" />
                </div>
                <h1 className="text-3xl font-serif text-sage-700 mb-2">
                  Détail de la consultation
                </h1>
                <p className="text-sage-600 mb-8">
                  Historique de votre rendez-vous passé.
                </p>
            </>
        )}

        {/* Détails du RDV */}
        <div className="bg-sage-50 rounded-xl p-6 text-left space-y-4 mb-8">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-sage-400 mt-0.5" />
            <div>
              <p className="text-sm text-sage-500">Date</p>
              <p className="font-medium text-sage-700">
                {new Date(appointment.date).toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-sage-400 mt-0.5" />
            <div>
              <p className="text-sm text-sage-500">Heure</p>
              <p className="font-medium text-sage-700">
                {appointment.start_time?.slice(0, 5)} - {appointment.end_time?.slice(0, 5)}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-sage-400 mt-0.5" />
            <div>
              <p className="text-sm text-sage-500">Lieu</p>
              <p className="font-medium text-sage-700">
                {getConsultationLocation(appointment.consultation_type)}
              </p>
            </div>
          </div>
        </div>

          {/* Actions conditionnelles (Seulement si le RDV est dans le futur !) */}
          {isFuture ? (
            <div className="space-y-8">
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  {/* BOUTON VIDEO */}
                  {appointment.meeting_url && (
                    <a 
                      href={appointment.meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn bg-purple-600 text-white hover:bg-purple-700 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-full sm:w-auto"
                    >
                      <Video className="w-4 h-4" />
                      Rejoindre la visio
                    </a>
                  )}
                  <a 
                    href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=Consultation+${encodeURIComponent(profile.admin_title || 'Santé')}&dates=${appointment.date.replace(/-/g, '')}T${appointment.start_time.replace(':', '')}00/${appointment.date.replace(/-/g, '')}T${appointment.end_time.replace(':', '')}00&details=Rendez-vous+avec+${encodeURIComponent(profile.admin_name || 'votre praticien')}&location=${encodeURIComponent(getConsultationLocation(appointment.consultation_type))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ajouter au calendrier
                  </a>
                  
                  {appointment.can_cancel && (
                    <Link 
                      to={`/cancel/${token}`}
                      className="btn btn-secondary text-red-600 hover:bg-red-50 w-full sm:w-auto"
                    >
                      Modifier / Annuler
                    </Link>
                  )}
                </div>

                {/* Rappel (Inclus uniquement pour les RDV futurs) */}
                <div className="pt-6 border-t border-sage-100">
                  <p className="text-sm text-sage-500">
                    📱 Un rappel SMS vous sera envoyé 48h avant votre rendez-vous.
                  </p>
                </div>
            </div>
        ) : (
            <div className="text-sm font-medium text-sage-600 bg-sage-50 border border-sage-100 p-4 rounded-xl mt-4">
                Cette consultation a déjà eu lieu. Vous pouvez retrouver votre facture dans votre espace patient.
            </div>
        )}

      </div>
    </div>
  )
}

